import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Edit, MessageSquare } from 'lucide-react';

// Helper function to format dates
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const CommunicationEditDialog = ({
  open,
  onOpenChange,
  selectedCommunication,
  communicationForm,
  setCommunicationForm,
  formLoading,
  onSave
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {selectedCommunication ? 'Edit Communication' : 'Add Communication'}
          </DialogTitle>
          <DialogDescription>Log a communication or action for this LLC</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={communicationForm.date}
                onChange={(e) => setCommunicationForm({ ...communicationForm, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>From</Label>
              <Input
                value={communicationForm.created_by_name || ''}
                onChange={(e) => setCommunicationForm({ ...communicationForm, created_by_name: e.target.value })}
                placeholder="Posted by"
                data-testid="comm-from-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>To</Label>
            <Input
              value={communicationForm.to_party}
              onChange={(e) => setCommunicationForm({ ...communicationForm, to_party: e.target.value })}
              placeholder="e.g., Bank, Accountant, Tenant"
            />
          </div>
          <div className="space-y-2">
            <Label>About</Label>
            <Input
              value={communicationForm.about}
              onChange={(e) => setCommunicationForm({ ...communicationForm, about: e.target.value })}
              placeholder="Subject or topic"
            />
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={communicationForm.notes}
              onChange={(e) => setCommunicationForm({ ...communicationForm, notes: e.target.value })}
              placeholder="Additional details"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSave} 
            disabled={formLoading || !communicationForm.to_party || !communicationForm.about}
          >
            {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CommunicationViewDialog = ({
  open,
  onOpenChange,
  viewingCommunication,
  canEdit,
  onEditClick
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Communication Details
          </DialogTitle>
        </DialogHeader>
        {viewingCommunication && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Date</Label>
                <p className="font-mono">{formatDate(viewingCommunication.date)}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Posted By</Label>
                <p>{viewingCommunication.created_by_name}</p>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">To</Label>
              <p className="font-medium">{viewingCommunication.to_party}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">About</Label>
              <p className="font-semibold text-slate-900">{viewingCommunication.about}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-200 max-h-[300px] overflow-y-auto">
                <p className="text-slate-700 whitespace-pre-wrap">{viewingCommunication.notes || 'No notes'}</p>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {canEdit && (
            <Button onClick={onEditClick}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
